"use client";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import type React from "react";
import { useApp } from "@/forApp/states/appState/appState";
import styles from "./styles.module.css";

interface LinkPaginationProps {
	currentPage: number;
	totalItems: number;
	itemsPerPage: number;
	basePath: string;
	maxVisiblePages?: number; // optional, defaults to 5
}

const LinkPagination: React.FC<LinkPaginationProps> = ({
	currentPage,
	totalItems,
	itemsPerPage,
	basePath,
	maxVisiblePages = 5,
}) => {
	const searchParams = useSearchParams();
	const { setIsLoading } = useApp();
	const totalPages = Math.ceil(totalItems / itemsPerPage);

	if (totalPages <= 1) {
		return null;
	}

	const createPageUrl = (page: number) => {
		// Create a new URLSearchParams instance
		const params = new URLSearchParams();

		// Copy all existing search params except 'page'
		searchParams.forEach((value, key) => {
			if (key !== "page") {
				params.append(key, value);
			}
		});

		// Set the page parameter
		params.set("page", page.toString());

		// Return the URL with the path and search params
		return `${basePath}?${params.toString()}`;
	};

	const getPageNumbers = () => {
		const pageNumbers: (number | "ellipsis1" | "ellipsis2")[] = [];

		// If few pages, show all
		if (totalPages <= maxVisiblePages) {
			for (let i = 1; i <= totalPages; i++) {
				pageNumbers.push(i);
			}
			return pageNumbers;
		}

		const windowSize = Math.max(1, Math.floor(maxVisiblePages));
		const half = Math.floor((windowSize - 1) / 2);

		let startPage = Math.max(1, currentPage - half);
		let endPage = startPage + windowSize - 1;

		if (endPage > totalPages) {
			endPage = totalPages;
			startPage = Math.max(1, endPage - windowSize + 1);
		}

		// Add first page and leading ellipsis if needed
		if (startPage > 1) {
			pageNumbers.push(1);
			if (startPage > 2) {
				pageNumbers.push("ellipsis1");
			}
		}

		for (let i = startPage; i <= endPage; i++) {
			pageNumbers.push(i);
		}

		// Add trailing ellipsis and last page if needed
		if (endPage < totalPages) {
			if (endPage < totalPages - 1) {
				pageNumbers.push("ellipsis2");
			}
			pageNumbers.push(totalPages);
		}

		return pageNumbers;
	};

	return (
		<div
			className={`${styles.paginationContainer} ${maxVisiblePages > 4 || maxVisiblePages <= 3 ? styles.compact : ""}`}
		>
			<Link
				href={currentPage > 1 ? createPageUrl(currentPage - 1) : "#"}
				className={`${styles.paginationButton} ${currentPage === 1 ? styles.disabled : ""}`}
				aria-disabled={currentPage === 1}
				onClick={(e) => {
					if (currentPage === 1) {
						e.preventDefault();
						return;
					}
					setIsLoading(true);
				}}
			>
				{maxVisiblePages > 4 ? "Назад" : "<"}
			</Link>

			{getPageNumbers().map((pageNumber, index) => {
				if (pageNumber === "ellipsis1" || pageNumber === "ellipsis2") {
					return (
						<span
							key={`ellipsis-${index}`}
							className={styles.paginationEllipsis}
						>
							...
						</span>
					);
				}

				return (
					<Link
						key={`page-${pageNumber}`}
						href={createPageUrl(pageNumber as number)}
						className={`${styles.paginationPageButton} ${currentPage === pageNumber ? styles.activePage : ""}`}
						aria-current={currentPage === pageNumber ? "page" : undefined}
						onClick={(e) => {
							if (currentPage === pageNumber) {
								e.preventDefault();
								return;
							}
							setIsLoading(true);
						}}
					>
						{pageNumber}
					</Link>
				);
			})}

			<Link
				href={currentPage < totalPages ? createPageUrl(currentPage + 1) : "#"}
				className={`${styles.paginationButton} ${currentPage >= totalPages ? styles.disabled : ""}`}
				aria-disabled={currentPage >= totalPages}
				onClick={(e) => {
					if (currentPage >= totalPages) {
						e.preventDefault();
						return;
					}
					setIsLoading(true);
				}}
			>
				{maxVisiblePages > 4 ? "Дальше" : ">"}
			</Link>
		</div>
	);
};

export default LinkPagination;
